<!--This component displays the statuses for a given plan holder -->
<template>
	<div
		class="flex flex-col gap-1 rounded-xl border border-slate-300 bg-slate-50 p-1"
	>
		<div v-for="(submission, name) in mappedSubmissions">
			<SubmissionStatusLine v-bind="{ ...submission?.getLineProps() }">
				{{ submission?.getVendorName() ?? defaultNames[name] }}
				<!--	Action button(s) theme is calculated in SubmissionStatusLine.vue			-->
				<template #actions="{ theme }">
					<button
						v-for="action in getModalActions(submission)"
						:key="action.buttonText"
						type="button"
						class="flex shrink-0 items-center gap-1 rounded-md border px-3 py-1 disabled:cursor-wait disabled:opacity-70"
						:class="[
							theme.buttonBackground,
							theme.buttonBorder,
							theme.buttonText,
						]"
						:disabled="isRetrying(name, action)"
						@click="handleAction(action, name)"
					>
						<ArrowPathIcon
							v-if="isRetrying(name, action)"
							class="size-4 animate-spin"
						/>
						{{ isRetrying(name, action) ? 'Retrying…' : action.buttonText }}
					</button>
				</template>
			</SubmissionStatusLine>
		</div>

		<SubmissionStatusLine
			v-if="showEmailSubmission"
			v-bind="{ ...emailSubmission.getLineProps() }"
		>
			{{ emailSubmission.getVendorName() }}
			<template #actions="{ theme }">
				<button
					v-for="action in getModalActions(emailSubmission)"
					:key="action.buttonText"
					type="button"
					class="flex shrink-0 items-center gap-1 rounded-md border px-3 py-1 disabled:cursor-wait disabled:opacity-70"
					:class="[
						theme.buttonBackground,
						theme.buttonBorder,
						theme.buttonText,
					]"
					:disabled="isRetrying('email', action)"
					@click="handleAction(action, 'email')"
				>
					<ArrowPathIcon
						v-if="isRetrying('email', action)"
						class="size-4 animate-spin"
					/>
					{{ isRetrying('email', action) ? 'Retrying…' : action.buttonText }}
				</button>
			</template>
		</SubmissionStatusLine>
	</div>
	<PrecoaModal
		v-model:open="actionModalOpen"
		size="medium"
		@close="closeActionModal"
	>
		<component
			:is="activeAction.component"
			v-if="activeAction?.component"
			v-bind="{ ...activeAction.componentProps, planId: planId }"
			@closeModal="closeActionModal"
		/>
	</PrecoaModal>
</template>

<script setup lang="ts">
import { EmailStatusMapper } from '@/classes/SubmissionStatuses/EmailStatusMapper'
import { EverplansStatusMapper } from '@/classes/SubmissionStatuses/EverplansStatusMapper'
import { InsuranceStatusMapper } from '@/classes/SubmissionStatuses/InsuranceStatusMapper'
import { OapStatusMapper } from '@/classes/SubmissionStatuses/OapStatusMapper'
import type { SubmissionStatusMapper } from '@/classes/SubmissionStatuses/SubmissionStatusMapper'
import type {
	EverplansSubmissionStatus,
	OapSubmissionStatus,
	SubmissionAction,
	SubmissionStatusGroup,
} from '@/classes/SubmissionStatuses/types'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import SubmissionStatusLine from '@/components/home/SubmissionStatuses/SubmissionStatusLine.vue'
import type { SubmissionChannel } from '@/services/types'
import { useAuthStore } from '@/stores/auth'
import { useSubmissionStore } from '@/stores/submission'
import type { UploadItem } from '@/types'
import { ArrowPathIcon } from '@heroicons/vue/24/solid'
import { computed, onMounted, type PropType, ref, shallowRef } from 'vue'

const submissionStore = useSubmissionStore()

const outgoingSubmissionStatuses = ref<
	{
		submissionChannel: SubmissionChannel
		status: UploadItem<any>['uploadStatus']
	}[]
>([])

type Vendor = keyof SubmissionStatusGroup

const props = defineProps({
	planId: {
		type: String,
		required: true,
	},
	planSubmissions: {
		type: Object as PropType<SubmissionStatusGroup>,
		required: true,
	},
})

const emit = defineEmits(['refreshStatus'])

const { isEverplansAuthorized } = useAuthStore()

const allVendors: Array<Vendor> = ['insurance', 'everplans', 'oap']

const mappedSubmissions = computed(() => {
	const vendorsToShow = isEverplansAuthorized
		? allVendors
		: allVendors.filter((vendor) => vendor !== 'everplans')

	return vendorsToShow.reduce(
		(acc, vendor: Vendor) => {
			acc[vendor] = getMapper(vendor as Vendor)
			return acc
		},
		{} as Record<Vendor, SubmissionStatusMapper | undefined>
	)
})

const emailSubmission = computed(
	() => new EmailStatusMapper(outgoingSubmissionStatuses.value)
)

const showEmailSubmission = computed(
	() => emailSubmission.value.getStatusText() !== 'None Sent'
)

const defaultNames = {
	insurance: 'NGL',
	everplans: 'Everplans',
	oap: 'OAP',
} as Record<Vendor, string>

const actionModalOpen = ref(false)
const activeAction = shallowRef<SubmissionAction>()
const retryingActionKey = ref<string>()

const getModalActions = (submission?: SubmissionStatusMapper) => {
	return submission?.getActions() ?? []
}

const getActionKey = (vendor: string, action: SubmissionAction) =>
	`${vendor}-${action.buttonText}`

const isRetrying = (vendor: string, action: SubmissionAction) =>
	retryingActionKey.value === getActionKey(vendor, action)

/**
 * Handles a submission action by opening its modal or running its action,
 * then refreshes the local submission statuses and remote status display.
 */
const handleAction = async (action: SubmissionAction, vendor: string) => {
	if (!action.onClick) {
		openActionModal(action)
		return
	}

	const actionKey = getActionKey(vendor, action)
	retryingActionKey.value = actionKey

	try {
		await action.onClick(props.planId)

		await submissionStore.refreshFailedRequests()
		await loadOutgoingSubmissionStatuses()

		emit('refreshStatus')
	} finally {
		retryingActionKey.value = undefined
	}
}

const openActionModal = (action: SubmissionAction) => {
	activeAction.value = action
	actionModalOpen.value = true
}

const closeActionModal = () => {
	actionModalOpen.value = false
	activeAction.value = undefined
	emit('refreshStatus')
}

const getMapper = (vendor: Vendor) => {
	const vendorSubmission = props.planSubmissions[vendor]

	switch (vendor) {
		case 'insurance':
			return new InsuranceStatusMapper(
				vendorSubmission,
				outgoingSubmissionStatuses.value
			)

		case 'oap':
			return new OapStatusMapper(
				vendorSubmission as OapSubmissionStatus,
				outgoingSubmissionStatuses.value
			)

		case 'everplans':
			return new EverplansStatusMapper(
				vendorSubmission as EverplansSubmissionStatus,
				outgoingSubmissionStatuses.value
			)
		default:
			return undefined
	}
}

/**
 * Loads the outgoing submission statuses for the current plan holder.
 */
const loadOutgoingSubmissionStatuses = async () => {
	outgoingSubmissionStatuses.value =
		await submissionStore.getSubmissionStatusById(props.planId)
}

onMounted(() => {
	loadOutgoingSubmissionStatuses()
})
</script>
